/**
 * The search body shared by every player-search surface — toolbar, active
 * filter chips, inline filter panel, and either the curated sections or the
 * results list.
 *
 * **Extracted from `GlobalSearchModalView` by SE-7242, and deliberately
 * chrome-free.** The modal view still renders exactly what it always did; it
 * now renders this inside its `ModalBody` instead of spelling the same tree
 * out inline. The extraction exists because quick capture needs this body on
 * two different hosts:
 *
 * - **Desktop** hosts it in the same `Modal` as everything else.
 * - **Mobile** hosts it in `MobileSheetChrome` (SE-5081), the sanctioned
 *   native bottom sheet. Nesting a `Modal` inside that Actionsheet would give
 *   two backdrops, two dismiss gestures and two scrollers, so the chrome has
 *   to be separable from the body rather than baked into it.
 *
 * `isVirtualized` is the other half of that: `MobileSheetChrome`'s body IS a
 * `ScrollView`, and a virtualized list inside a scroller has no measurable
 * viewport to virtualize against. On that host the rows render plainly, which
 * is what the shipped `MobileAddPlayerSearchSurfaceView` already does inside
 * the same chrome.
 * @module features/global-search/components/organisms/GlobalSearchBody/GlobalSearchBodyView
 */

import React, { memo } from "react";

import GlobalSearchCuratedSections from "@/features/global-search/components/organisms/GlobalSearchCuratedSections";

import ActiveFilterChips from "@/components/organisms/shared/search-modal/ActiveFilterChips";
import InlineFilterPanel from "@/components/organisms/shared/search-modal/InlineFilterPanel";
import PlayerSearchResultsList from "@/components/organisms/shared/search-modal/PlayerSearchResultsList";
import SearchToolbar from "@/components/organisms/shared/search-modal/SearchToolbar";
import SortTrigger from "@/components/organisms/shared/search-modal/SortTrigger";
import type { SortDropdownProps } from "@/components/organisms/shared/search-modal/SortTrigger";
import type { NoPlayersFoundActionPlacement } from "@/components/organisms/shared/search-modal/NoPlayersFound/NoPlayersFoundView";
import { Stack } from "@/components/atoms/Stack";

import { PlayerSearchPlayerFragment } from "@/generated/graphql";
import type { FilterState } from "@/hooks/useFilterState";
import type { FilterOption } from "@/types/filter";

/** Subtitle copy shown when an active search produces no results */
export const EMPTY_RESULTS_SUBTITLE = "Try a different name or club.";

/**
 * Heading shown when the search stopped because the client-side auto-load
 * walk exhausted its budget (SE-8102's `MAX_AUTO_LOAD_PAGES`) while the
 * server still reports more pages, rather than because there is genuinely no
 * match — an honest "we stopped searching" instead of a confident "no
 * players found" (SE-8324).
 */
export const AUTO_LOAD_BUDGET_EMPTY_HEADING = "Search stopped early";

/** Subtitle paired with {@link AUTO_LOAD_BUDGET_EMPTY_HEADING} */
export const AUTO_LOAD_BUDGET_EMPTY_SUBTITLE =
  "We stopped searching before checking every match for this filter. Try narrowing your search term or filters.";

/** Props for GlobalSearchBodyView */
export interface GlobalSearchBodyViewProps {
  /** Whether the hosting surface is open — gates the curated sections' queries */
  readonly isOpen: boolean;
  /** Handler to close the hosting surface */
  readonly onClose: () => void;
  /** Current search query text */
  readonly searchQuery: string;
  /** Handler for search text changes */
  readonly onSearchChange: (text: string) => void;
  /** Handler to clear search text */
  readonly onClearSearch: () => void;
  /** Number of active filters */
  readonly activeFilterCount: number;
  /** Handler to toggle the inline filter panel */
  readonly onToggleFilterPanel: () => void;
  /** Whether the inline filter panel is visible */
  readonly isFilterPanelOpen: boolean;
  /** Filter state from useFilterState */
  readonly filterState: FilterState;
  /** Derived position filter options */
  readonly positionOptions: readonly FilterOption[];
  /** Derived club filter options */
  readonly clubOptions: readonly FilterOption[];
  /** Active sort field for metric badge display */
  readonly effectiveSortBy: string;
  /** Current sort field display label */
  readonly sortFieldLabel: string;
  /** Toggle sort dropdown */
  readonly onToggleSort: () => void;
  /** Sort dropdown props */
  readonly sortDropdownProps: SortDropdownProps;
  /** Player results to display */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether data is loading */
  readonly loading: boolean;
  /**
   * Whether a NEW query is in flight while the previous results are still
   * displayed (SE-7374). Forwarded to the results list, which shows an inline
   * refresh strip instead of blanking the panel.
   */
  readonly isRefreshing?: boolean;
  /** Whether more pages are available */
  readonly hasMore: boolean;
  /** Triggers next page fetch */
  readonly loadMore: () => void;
  /** Whether a fetchMore request is currently in progress */
  readonly isLoadingMore: boolean;
  /** Whether results are empty */
  readonly isEmpty: boolean;
  /**
   * Whether the empty state above is caused by the client-side auto-load
   * walk exhausting its budget rather than a genuine zero-match search
   * (SE-8324) — drives a distinct empty-state message instead of an
   * indistinguishable "no players found".
   */
  readonly isEmptyDueToAutoLoadBudget?: boolean;
  /** Whether the search query returned an error */
  readonly isError: boolean;
  /** Handler for player row press */
  readonly onPlayerPress: (item: PlayerSearchPlayerFragment) => void;
  /** Active filter chip descriptors */
  readonly activeFilterChips: readonly {
    readonly key: string;
    readonly label: string;
  }[];
  /** Pre-built chip removal handlers keyed by chip key */
  readonly chipRemoveHandlers: Readonly<Record<string, () => void>>;
  /** Handler to close the inline filter panel */
  readonly onCloseFilterPanel: () => void;
  /** Whether the user has initiated a search */
  readonly hasSearched: boolean;
  /**
   * Transfermarkt ID that resolved to no player (SE-3599); switches the
   * empty state to the ID-specific not-found message
   */
  readonly transfermarktNotFoundId?: number | null;
  /** Optional ManuallyAddPlayer option rendered in the empty state */
  readonly manuallyAddPlayerOption?: React.ReactNode;
  /** Optional ManuallyAddPlayer option rendered as a list footer when results exist */
  readonly manuallyAddPlayerFooterOption?: React.ReactNode;
  /** Renders the action slot for each player row */
  readonly renderAction: (
    player: PlayerSearchPlayerFragment
  ) => React.ReactNode;
  /**
   * When true (empty search + no filters), the results pane shows the curated
   * "Recently Viewed" / "Upside Potential" sections instead of search results.
   */
  readonly showCurated?: boolean;
  /** Whether a given row accepts a press (default: every row does) */
  readonly isRowPressable?: (player: PlayerSearchPlayerFragment) => boolean;
  /** Where the empty state's action sits relative to its message */
  readonly emptyActionPlacement?: NoPlayersFoundActionPlacement;
  /**
   * Whether the results list may own a virtualized scroller. Defaults to
   * true, which is what a modal host wants. Hosts that are themselves a
   * scroller (`MobileSheetChrome`) pass false — see the module docblock.
   */
  readonly isVirtualized?: boolean;
  /** Test identity for the search field */
  readonly searchInputTestID?: string;
  /**
   * Accessible name for the search field. Gluestack's `InputField` names
   * itself from `aria-label` and announces as "Input Field" without one.
   */
  readonly searchInputAccessibilityLabel?: string;
  /**
   * Whether the facet controls belong on this host.
   *
   * True everywhere the toolbar has a modal's width to spend. FALSE on the
   * mobile add-player sheet, and the reason is measured rather than aesthetic:
   * the row is `[input flex-1] [Filters] [Sort] [Close]` inside `px-6 gap-3`,
   * and at sheet width the three buttons keep their intrinsic size while the
   * input — which has no width floor — collapses to a ~50px sliver that cannot
   * be typed into. Captured on an S24 (SE-7963).
   *
   * Frame 33 draws that sheet with a full-width input and no facet controls at
   * all, so hiding them is what the design asks for AND what makes the input
   * usable. A flag on the shared toolbar rather than a second toolbar: SE-7242
   * chose to share this body precisely so the sheet would not grow a parallel
   * implementation of it.
   */
  readonly showFacets?: boolean;
}

/**
 * Renders the shared search body.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the hosting surface is open
 * @param root0.onClose - Close handler
 * @param root0.searchQuery - Current search text
 * @param root0.onSearchChange - Search text change handler
 * @param root0.onClearSearch - Clear search handler
 * @param root0.activeFilterCount - Number of active filters
 * @param root0.onToggleFilterPanel - Toggle inline filter panel
 * @param root0.isFilterPanelOpen - Whether filter panel is open
 * @param root0.filterState - Filter state hook return value
 * @param root0.positionOptions - Derived position options
 * @param root0.clubOptions - Derived club options
 * @param root0.effectiveSortBy - Active sort field for metric badge display
 * @param root0.sortFieldLabel - Current sort field display label
 * @param root0.onToggleSort - Toggle sort dropdown handler
 * @param root0.sortDropdownProps - Sort dropdown props
 * @param root0.players - Player results
 * @param root0.loading - Whether loading
 * @param root0.isRefreshing - Whether a newer query is in flight over existing results
 * @param root0.hasMore - Whether more pages are available
 * @param root0.loadMore - Load more handler
 * @param root0.isLoadingMore - Whether a fetchMore request is in progress
 * @param root0.isEmpty - Whether results are empty
 * @param root0.isEmptyDueToAutoLoadBudget - Whether the empty state came from the auto-load budget rather than a genuine zero-match search
 * @param root0.isError - Whether the search query returned an error
 * @param root0.onPlayerPress - Player press handler
 * @param root0.activeFilterChips - Active filter chip descriptors
 * @param root0.chipRemoveHandlers - Pre-built chip removal handlers
 * @param root0.onCloseFilterPanel - Handler to close the inline filter panel
 * @param root0.hasSearched - Whether the user has initiated a search
 * @param root0.transfermarktNotFoundId - Transfermarkt ID with no matching player
 * @param root0.manuallyAddPlayerOption - Optional ManuallyAddPlayer option for empty state
 * @param root0.manuallyAddPlayerFooterOption - Optional ManuallyAddPlayer option for list footer
 * @param root0.renderAction - Renders the action slot for each player row
 * @param root0.showCurated - Whether to render curated sections instead of results
 * @param root0.isRowPressable - Whether a given row accepts a press
 * @param root0.emptyActionPlacement - Where the empty state's action sits
 * @param root0.isVirtualized - Whether the results list owns a scroller
 * @param root0.searchInputTestID - Test identity for the search field
 * @param root0.showFacets - Whether the facet controls belong on this host
 * @param root0.searchInputAccessibilityLabel - Accessible name for the search field
 */
const GlobalSearchBodyView = ({
  isOpen,
  onClose,
  searchQuery,
  onSearchChange,
  onClearSearch,
  activeFilterCount,
  onToggleFilterPanel,
  isFilterPanelOpen,
  filterState,
  positionOptions,
  clubOptions,
  effectiveSortBy,
  sortFieldLabel,
  onToggleSort,
  sortDropdownProps,
  players,
  loading,
  isRefreshing,
  hasMore,
  loadMore,
  isLoadingMore,
  isEmpty,
  isEmptyDueToAutoLoadBudget = false,
  isError,
  onPlayerPress,
  activeFilterChips,
  chipRemoveHandlers,
  onCloseFilterPanel,
  hasSearched,
  transfermarktNotFoundId,
  manuallyAddPlayerOption,
  manuallyAddPlayerFooterOption,
  renderAction,
  showCurated = false,
  isRowPressable,
  emptyActionPlacement,
  isVirtualized,
  searchInputTestID,
  searchInputAccessibilityLabel,
  showFacets,
}: GlobalSearchBodyViewProps) => (
  <Stack className="min-h-0 flex-1">
    <SearchToolbar
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      onClearSearch={onClearSearch}
      onClose={onClose}
      activeFilterCount={activeFilterCount}
      onToggleFilterPanel={onToggleFilterPanel}
      searchInputTestID={searchInputTestID}
      searchInputAccessibilityLabel={searchInputAccessibilityLabel}
      showFacets={showFacets}
      sortTrigger={
        <SortTrigger
          onToggleSort={onToggleSort}
          sortDropdownProps={sortDropdownProps}
          sortFieldLabel={sortFieldLabel}
        />
      }
    />
    <ActiveFilterChips
      chips={activeFilterChips}
      removeHandlers={chipRemoveHandlers}
    />
    <Stack direction="horizontal" className="min-h-0 flex-1 items-stretch">
      {isFilterPanelOpen ? (
        <InlineFilterPanel
          /**
           * Re-key on the committed active-filter signature so the
           * panel's draft re-snapshots committed whenever a filter is
           * removed via an active chip while the panel is open (SE-5162).
           * Without this, the stale draft is re-committed on "Show
           * Results" and the removed filter reappears. The signature is
           * stable during normal panel editing (which mutates the draft,
           * not committed), so this never remounts mid-edit.
           */
          key={activeFilterChips
            .map(chip => `${chip.key}:${chip.label}`)
            .join("|")}
          filterState={filterState}
          positionOptions={positionOptions}
          clubOptions={clubOptions}
          onClose={onCloseFilterPanel}
        />
      ) : null}
      <Stack className="min-h-0 flex-1">
        {showCurated ? (
          <GlobalSearchCuratedSections
            isOpen={isOpen}
            onPlayerPress={onPlayerPress}
            renderAction={renderAction}
          />
        ) : hasSearched ? (
          <PlayerSearchResultsList
            players={players}
            loading={loading}
            isRefreshing={isRefreshing}
            loadMore={loadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isEmpty={isEmpty}
            isError={isError}
            onPlayerPress={onPlayerPress}
            effectiveSortBy={effectiveSortBy}
            sortFieldLabel={sortFieldLabel}
            manuallyAddPlayerOption={manuallyAddPlayerOption}
            manuallyAddPlayerFooterOption={manuallyAddPlayerFooterOption}
            renderAction={renderAction}
            isRowPressable={isRowPressable}
            emptyActionPlacement={emptyActionPlacement}
            isVirtualized={isVirtualized}
            emptyHeading={
              transfermarktNotFoundId == null && isEmptyDueToAutoLoadBudget
                ? AUTO_LOAD_BUDGET_EMPTY_HEADING
                : undefined
            }
            emptySubtitle={
              transfermarktNotFoundId != null
                ? `No player found for Transfermarkt ID ${transfermarktNotFoundId}`
                : isEmptyDueToAutoLoadBudget
                  ? AUTO_LOAD_BUDGET_EMPTY_SUBTITLE
                  : EMPTY_RESULTS_SUBTITLE
            }
          />
        ) : null}
      </Stack>
    </Stack>
  </Stack>
);

GlobalSearchBodyView.displayName = "GlobalSearchBodyView";

export default memo(GlobalSearchBodyView);
